import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/bits";
import LeadDialog from "@/components/LeadDialog";
import { submitLead } from "@/lib/api";
import { CONTACT } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";

const field =
  "w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-isav-green transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", county: "", project_details: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    setLoading(true);
    try {
      await submitLead({ kind: "contact", ...form });
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", county: "", project_details: "" });
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="pt-32">
      <header className="mx-auto max-w-[1600px] px-6 pb-16 md:px-12">
        <Reveal>
          <SectionLabel>Contact</SectionLabel>
          <h1 className="mt-8 max-w-5xl font-serif text-5xl font-300 leading-[0.98] md:text-8xl">
            Begin the <span className="italic accent-text">conversation</span>.
          </h1>
        </Reveal>
      </header>

      <section className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 pb-32 md:px-12 lg:grid-cols-12 lg:gap-24">
        <Reveal className="lg:col-span-5">
          <p className="font-serif text-2xl font-300 leading-snug text-white/85 md:text-3xl">
            Book a private consultation, request a project quote, or simply say hello.
          </p>

          <div className="mt-12 space-y-6">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="group flex items-center gap-4 border-b border-white/10 pb-5 transition-colors hover:border-white/40">
              <Phone className="h-5 w-5 text-isav-green" />
              <span className="text-lg text-white/80 group-hover:text-white">{CONTACT.phone}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="group flex items-center gap-4 border-b border-white/10 pb-5 transition-colors hover:border-white/40">
              <Mail className="h-5 w-5 text-isav-green" />
              <span className="text-lg text-white/80 group-hover:text-white">{CONTACT.email}</span>
            </a>
            <div className="flex items-start gap-4 border-b border-white/10 pb-5">
              <MapPin className="mt-1 h-5 w-5 text-isav-green" />
              <div>
                <p className="text-white/80">Service Areas</p>
                <p className="mt-1 text-sm text-white/50">{CONTACT.serviceAreas.join(" · ")}</p>
              </div>
            </div>
          </div>

          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Licensed low-voltage integrator · CSLB #{CONTACT.license}</p>

          <div className="mt-10 flex flex-wrap gap-4">
            <LeadDialog kind="consultation" variant="primary" testId="contact-book-consultation" />
            <LeadDialog kind="quote" variant="secondary" testId="contact-request-quote" />
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="border border-white/10 bg-isav-bg2 p-8 md:p-12">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Name</Label>
                <Input data-testid="contact-name" className={field} value={form.name} onChange={set("name")} placeholder="Full name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Email</Label>
                <Input data-testid="contact-email-input" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Phone</Label>
                <Input data-testid="contact-phone-input" className={field} value={form.phone} onChange={set("phone")} placeholder="Optional" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">County</Label>
                <Select value={form.county} onValueChange={set("county")}>
                  <SelectTrigger data-testid="contact-county" className={`${field} justify-between`}>
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent className="bg-isav-bg2 border-white/10 text-white rounded-none">
                    {CONTACT.serviceAreas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-8">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">How can we help?</Label>
              <Textarea data-testid="contact-message" className={`${field} min-h-[120px]`} value={form.project_details} onChange={set("project_details")} placeholder="Tell us about your home and vision…" />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group mt-10 inline-flex w-full items-center justify-center gap-2 bg-white px-8 py-4 text-xs font-500 uppercase tracking-[0.2em] text-isav-bg transition-all hover:bg-white/85 disabled:opacity-60 sm:w-auto"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
              {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
